import React, { useEffect, useState } from "react";
import { Flag, MessageSquareWarning, Inbox, MapPin, LocateFixed, Building2, Send, X, CheckCircle2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccBtnPink, ccBtnBlue, ccBtnGhost, EmptyState } from "../care/ui";
import { getDeviceLocation } from "../lib/location";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  open: "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]",
  responded: "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]",
  resolved: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
};

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// ---------- Facility-side complaints manager (+ enrollment inquiries) ----------
export const ComplaintsManager = () => {
  const [complaints, setComplaints] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = async () => {
    try {
      const [c, i] = await Promise.all([
        fetch(`${API}/api/complaints`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${API}/api/enroll/inquiries`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
      ]);
      setComplaints(c);
      setInquiries(i);
    } catch (err) {
      console.error(err);
      toast.error("Could not load complaints.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (id, action) => {
    try {
      const res = await fetch(`${API}/api/complaints/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, response: replyFor === id ? replyText : "" }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Update failed");
      toast.success(action === "resolve" ? "Complaint resolved." : "Response sent.");
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (complaints === null) return <p className="text-sm text-[#9CA3AF] font-semibold py-6">Loading complaints…</p>;

  return (
    <div className="space-y-6" data-testid="ops-complaints">
      <div>
        <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-1">
          <MessageSquareWarning className="w-4 h-4 text-[#D9A93E]" /> Complaints Inbox
        </h4>
        <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
          Complaints from parents and staff route here first. Respond promptly — unresolved items escalate to NestTrack administration after 48 hours.
        </p>
        {complaints.length === 0 ? (
          <EmptyState text="No complaints filed. Keep up the great care!" testId="complaints-empty" />
        ) : (
          <div className="space-y-3">
            {complaints.map((c) => (
              <div key={c.id} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`complaint-row-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-black text-[#374151] dark:text-slate-100">{c.subject}</p>
                    <p className="text-[11px] text-[#9CA3AF] font-bold">
                      {c.fromName || c.fromEmail} · {c.fromRole} · filed {fmtTs(c.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.escalated && (
                      <span className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-extrabold uppercase rounded-full" data-testid={`complaint-escalated-${c.id}`}>
                        Escalated
                      </span>
                    )}
                    <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${STATUS_STYLES[c.status]}`} data-testid={`complaint-status-${c.id}`}>
                      {c.status}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[#6B7280] dark:text-slate-300 mt-2 leading-relaxed">{c.message}</p>
                {c.response && (
                  <p className="text-xs text-[#3D7E9E] bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-xl px-3 py-2 mt-2" data-testid={`complaint-response-${c.id}`}>
                    <span className="font-extrabold">Your response:</span> {c.response}
                  </p>
                )}
                {c.status !== "resolved" && (
                  <div className="mt-3 space-y-2">
                    {replyFor === c.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write your response to the family…"
                          className={ccInput}
                          data-testid={`complaint-response-input-${c.id}`}
                        />
                        <button onClick={() => act(c.id, "respond")} className={ccBtnBlue} data-testid={`complaint-send-${c.id}`}>
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => { setReplyFor(c.id); setReplyText(c.response || ""); }} className={ccBtnGhost} data-testid={`complaint-reply-${c.id}`}>
                          Respond
                        </button>
                        <button onClick={() => act(c.id, "resolve")} className={ccBtnPink} data-testid={`complaint-resolve-${c.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Resolve
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {c.status === "resolved" && (
                  <p className="text-[10px] text-[#2F6031] font-bold mt-2">Resolved {fmtTs(c.resolvedAt)}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-3">
          <Inbox className="w-4 h-4 text-[#5CB3DC]" /> Enrollment Inquiries ({inquiries.length})
        </h4>
        {inquiries.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="inquiries-empty">No inquiries yet — they appear when parents request enrollment from their portal.</p>
        ) : (
          <div className="space-y-2">
            {inquiries.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 bg-[#E6F2FA]/50 border border-[#B3E0F2] rounded-2xl px-4 py-3 dark:bg-[#273244]" data-testid={`inquiry-row-${i.id}`}>
                <div>
                  <p className="text-xs font-black text-[#374151] dark:text-slate-100">
                    {i.parentName || i.parentEmail}
                    {i.childName ? ` — for ${i.childName}` : ""}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] font-medium">{i.notes || "No notes"} · {fmtTs(i.createdAt)}</p>
                </div>
                <a href={`mailto:${i.parentEmail}`} className={`${ccBtnBlue} shrink-0`} data-testid={`inquiry-contact-${i.id}`}>
                  Contact
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Parent-side: file complaints + enroll directory ----------
export const ParentSupport = ({ currentUser }) => {
  const [complaints, setComplaints] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showDirectory, setShowDirectory] = useState(false);

  const load = () =>
    fetch(`${API}/api/complaints`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setComplaints)
      .catch((err) => console.error(err));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/complaints`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, message }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not file complaint");
      toast.success("Complaint sent to your daycare — they'll respond from their dashboard.");
      setSubject("");
      setMessage("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#FCE6E9] dark:border-[#374457]" data-testid="parent-support">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">Support &amp; Enrollment</p>
        <div className="flex gap-2">
          <button onClick={() => setShowDirectory(true)} className={ccBtnBlue} data-testid="enroll-open-btn">
            <Building2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Find Daycares Enrolling
          </button>
          <button onClick={() => setShowForm(!showForm)} className={ccBtnGhost} data-testid="complaint-toggle-btn">
            <Flag className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> File a Complaint
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 mb-4 space-y-3 dark:bg-[#273244] dark:border-[#374457]" data-testid="complaint-form">
          <div>
            <label className={ccLabel}>Subject</label>
            <input required type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={ccInput} data-testid="complaint-subject" placeholder="e.g. Pickup delay on Tuesday" />
          </div>
          <div>
            <label className={ccLabel}>What happened?</label>
            <textarea required rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className={ccInput} data-testid="complaint-message" placeholder="Describe the issue — it goes straight to your facility's dashboard." />
          </div>
          <button type="submit" className={ccBtnPink} data-testid="complaint-submit">Send to Facility</button>
        </form>
      )}

      {complaints && complaints.length > 0 && (
        <div className="space-y-2 mb-2" data-testid="my-complaints">
          {complaints.map((c) => (
            <div key={c.id} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-3.5 dark:bg-[#273244] dark:border-[#374457]" data-testid={`my-complaint-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-black text-[#374151] dark:text-slate-100">{c.subject}</p>
                <span className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${STATUS_STYLES[c.status]}`} data-testid={`my-complaint-status-${c.id}`}>
                  {c.status}
                </span>
              </div>
              {c.response && <p className="text-[11px] text-[#3D7E9E] mt-1.5"><span className="font-extrabold">Facility:</span> {c.response}</p>}
            </div>
          ))}
        </div>
      )}

      {showDirectory && <EnrollDirectory currentUser={currentUser} onClose={() => setShowDirectory(false)} />}
    </div>
  );
};

// ---------- Enroll directory modal (active facilities, nearest first) ----------
const EnrollDirectory = ({ onClose }) => {
  const [facilities, setFacilities] = useState(null);
  const [locating, setLocating] = useState(false);
  const [loc, setLoc] = useState(null);
  const [childName, setChildName] = useState("");
  const [notes, setNotes] = useState("");
  const [requested, setRequested] = useState({});

  const load = (coords) => {
    const qs = coords ? `?lat=${coords.lat}&lng=${coords.lng}` : "";
    fetch(`${API}/api/enroll/directory${qs}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFacilities)
      .catch((err) => {
        console.error(err);
        toast.error("Could not load the enrollment directory.");
      });
  };

  useEffect(() => {
    load(null);
  }, []);

  const useLocation = async () => {
    setLocating(true);
    try {
      const coords = await getDeviceLocation();
      setLoc(coords);
      load(coords);
      toast.success("Sorted by distance from you.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLocating(false);
    }
  };

  const request = async (facilityId) => {
    try {
      const res = await fetch(`${API}/api/enroll/request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ facilityId, childName, notes }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Request failed");
      setRequested((prev) => ({ ...prev, [facilityId]: true }));
      toast.success("Enrollment request sent — the facility will reach out.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#374151]/40 backdrop-blur-sm" onClick={onClose} data-testid="enroll-directory-modal">
      <div className="bg-white rounded-3xl border border-[#FCE6E9] shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 dark:bg-[#273244] dark:border-[#374457]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">Daycares Accepting Enrollment</h3>
          <button onClick={onClose} data-testid="enroll-close-btn" className="w-8 h-8 rounded-full bg-[#FFF9F2] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <button onClick={useLocation} disabled={locating} className={`${ccBtnBlue} mb-4`} data-testid="enroll-use-location">
          <LocateFixed className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> {locating ? "Locating…" : loc ? "Sorted nearest first ✓" : "Sort by Nearest"}
        </button>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input type="text" value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Child's name (optional)" className={ccInput} data-testid="enroll-child-name" />
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className={ccInput} data-testid="enroll-notes" />
        </div>
        {facilities === null ? (
          <p className="text-sm text-[#9CA3AF] font-semibold py-6 text-center">Loading facilities…</p>
        ) : facilities.length === 0 ? (
          <EmptyState text="No daycares are actively enrolling right now. Check back soon!" testId="enroll-empty" />
        ) : (
          <div className="space-y-3" data-testid="enroll-list">
            {facilities.map((f) => (
              <div key={f.facilityId} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`enroll-card-${f.facilityId}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-black text-[#374151] dark:text-slate-100">{f.facilityName}</p>
                  {f.distanceMiles !== null && (
                    <span className="flex items-center gap-1 text-[11px] font-extrabold text-[#3D7E9E]">
                      <MapPin className="w-3 h-3" /> {f.distanceMiles} mi
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#9CA3AF] font-medium mt-0.5">
                  {f.address || "Address on file"}
                  {f.openSpots !== null ? ` · ${f.openSpots} open spots` : ""}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => request(f.facilityId)}
                    disabled={requested[f.facilityId]}
                    className={`${ccBtnPink} flex-1 disabled:opacity-60`}
                    data-testid={`enroll-request-${f.facilityId}`}
                  >
                    {requested[f.facilityId] ? "Request Sent ✓" : "Request Enrollment"}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${API}/api/share/facility/${f.facilityId}`);
                      toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
                    }}
                    className="px-3.5 py-2.5 bg-[#E6F2FA] hover:bg-[#D6EBF7] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold transition-colors flex items-center gap-1.5"
                    data-testid={`enroll-share-${f.facilityId}`}
                    title="Copy public share link for this daycare"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
